import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { type AudienceData, AudienceOption } from '@openx/types/targeting/audience.ts';
import { noop } from '@openx/utils/noop.ts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { DimensionChipLine } from '../shared/DimensionChip/DimensionChipLine.tsx';
import { FiltersTitle } from '../shared/FiltersTitle/FiltersTitle.tsx';
import { SelectedItems } from '../shared/SelectedItems/SelectedItems.tsx';
import { Switch } from '../shared/Switch/Switch.tsx';
import type { TargetingItemsProps } from '../types.ts';
import { EidsItems } from './components/EidsItems.tsx';
import { MultiProviderAudienceAlert } from './components/MultiProviderAudienceAlert.tsx';
import { useAudienceItems } from './hooks/useAudienceItems.ts';
import { useAudienceTitles } from './hooks/useAudienceTitles.ts';

const StyledWrapper = styled(Box)`
  margin-left: ${({ theme }) => theme.spacing(3.5)};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

export const Items = ({
  targetingParams,
  readonly,
  accountUid,
  onChange = noop,
  onAllAnyOperatorChange = noop,
  isFromCurated = false,
  skipAlertRender = true,
}: TargetingItemsProps<AudienceData | null> & {
  accountUid: string;
  onAllAnyOperatorChange?: () => void;
  skipAlertRender?: boolean;
}) => {
  const { t } = useTranslation();
  const { useGetAudienceMultiproviderInfo } = useTargetingContext();
  const { titles } = useAudienceTitles();

  const {
    selectedAudience,
    selectedAudienceType,
    selectedTrafficFilterEidsArray,
    selectedAudienceOption,
    getSelectedItems,
    isLoading,
    audienceTypeLabels,
    optionsMap,
    filtersTitle,
    allAnySwitchState,
    DefaultItemComponent,
    isReadOnly,
    isMinimalAudiencePreview,
  } = useAudienceItems({
    targetingParams,
    accountUid,
    readonly,
    isFromCurated,
  });

  const { hasMultiProviderPricingData, loadingAudienceCheck } = useGetAudienceMultiproviderInfo({
    selectedAudienceOption,
    values: getSelectedItems(),
  });

  const handleClearAudienceType = useCallback(() => {
    onChange(params => ({ ...params, [AudienceOption.INTENDED_AUDIENCE]: undefined }));
  }, [onChange]);

  const handleClearAudienceOptions = useCallback(() => {
    onChange(params => ({ [AudienceOption.INTENDED_AUDIENCE]: params?.[AudienceOption.INTENDED_AUDIENCE] }));
  }, [onChange]);

  const handleChange = useCallback(
    (values: string[]) => {
      if (selectedAudienceOption !== AudienceOption.DMP_SEGMENTS || values.length === 0) {
        handleClearAudienceOptions();
        return;
      }

      if (selectedAudience) {
        onChange({
          [selectedAudienceOption as AudienceOption]: {
            ...selectedAudience[selectedAudienceOption],
            val: values.join(','),
          },
        });
      }
    },
    [handleClearAudienceOptions, onChange, selectedAudience, selectedAudienceOption]
  );

  if (isLoading || loadingAudienceCheck) {
    return <Loader />;
  }

  return (
    <div data-test="sub-section">
      {selectedAudienceOption && (
        <>
          <FiltersTitle
            onClear={handleClearAudienceOptions}
            title={titles[selectedAudienceOption]}
            readonly={isReadOnly}
          />

          {selectedAudienceOption === AudienceOption.DMP_SEGMENTS && (
            <Switch
              switchState={allAnySwitchState}
              onSwitchChange={onAllAnyOperatorChange}
              switchOffLabel={t('ALL')}
              switchOnLabel={t('ANY')}
              groupTitle={t('THE FOLLOWING:')}
              disabled={isReadOnly}
            />
          )}

          <SelectedItems
            filtersTitle={filtersTitle}
            isAddIcon
            selectedItems={getSelectedItems()}
            ItemComponent={DefaultItemComponent}
            readonly={isReadOnly}
            handleChange={handleChange}
            options={optionsMap}
            dataTest={selectedAudienceOption.replace('_', '-')}
            isMinimalAudiencePreview={isMinimalAudiencePreview}
          />

          {hasMultiProviderPricingData && !skipAlertRender && <MultiProviderAudienceAlert />}

          {!!selectedTrafficFilterEidsArray.length && (
            <StyledWrapper>
              <EidsItems
                selectedTrafficFilterEidsArray={selectedTrafficFilterEidsArray}
                readonly={isReadOnly}
                onChange={onChange}
              />
            </StyledWrapper>
          )}
        </>
      )}

      {!!selectedAudienceType && (
        <>
          {selectedAudienceOption && <DimensionChipLine />}

          <FiltersTitle
            onClear={handleClearAudienceType}
            title={titles[AudienceOption.INTENDED_AUDIENCE]}
            readonly={true}
          />

          <SelectedItems
            filtersTitle={t('Included')}
            isAddIcon
            selectedItems={[selectedAudienceType.val]}
            readonly={true}
            options={audienceTypeLabels}
            handleChange={handleClearAudienceType}
            dataTest="selected-audience-type"
          />
        </>
      )}
    </div>
  );
};
